/**
 * Onboarding Component
 * Editorial aesthetic — matches the app's typographic, intimate design language.
 * Collects birth data to unlock astrological features.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { useProfile } from '../../profile/context/ProfileContext';
import { saveConsent } from '../../profile/services/profileService';
import { checkOnboardingStatus as checkOnboardingStatusService } from '../services/onboardingService';
import { calculateZodiacSign } from '@/shared/hooks/useAIFeedback';
import CityAutocomplete from '../../profile/components/settings/CityAutocomplete';
import { Sun, Moon, Star } from 'lucide-react';
import './Onboarding.css';
import logger from '@/shared/utils/core/logger';
import { trackOnboardingStepViewed } from '@/shared/utils/analytics/analytics';

const ONBOARDING_STEPS = {
  WELCOME: 'welcome',
  BIRTH_DATE: 'birthDate',
  BIRTH_TIME: 'birthTime',
  BIRTH_PLACE: 'birthPlace',
  CONSENT: 'consent',
  CALCULATING: 'calculating',
  REVEAL: 'reveal'
};

export function Onboarding({ onComplete, onSkip }) {
  const { user } = useAuth();
  const { saveProfile, hexagramProfile } = useProfile();

  // Derive first name for personalization (works for both email and Google signups)
  const firstName = user?.displayName?.trim().split(/\s+/)[0] || null;

  const [currentStep, setCurrentStep] = useState(ONBOARDING_STEPS.WELCOME);
  const [formData, setFormData] = useState({
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    birthPlaceData: null,
    skipBirthTime: false
  });
  const [ageVerified, setAgeVerified] = useState(false);
  const [errors, setErrors] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);

  const stepStartTime = React.useRef(Date.now());
  const previousStep = React.useRef(null);

  useEffect(() => {
    const steps = Object.values(ONBOARDING_STEPS);
    const stepNumber = steps.indexOf(currentStep) + 1;
    const timeOnPreviousStep = previousStep.current ? Date.now() - stepStartTime.current : 0;

    if (previousStep.current !== currentStep) {
      trackOnboardingStepViewed(currentStep, { stepNumber, timeOnPreviousStep });
      previousStep.current = currentStep;
      stepStartTime.current = Date.now();
    }
  }, [currentStep]);

  useEffect(() => {
    if (user) checkOnboardingStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, onComplete]); // onComplete in deps — checkOnboardingStatus closes over it

  const checkOnboardingStatus = async () => {
    if (!user) { onComplete?.(); return; }
    try {
      const status = await checkOnboardingStatusService(user);
      if (!status.needsOnboarding) { onComplete?.(); return; }

      const onboardingCompleted = status.profileData?.onboarding?.completed ||
                                  status.profileData?.onboardingCompleted;
      const onboardingSkipped   = status.profileData?.onboarding?.skipped ||
                                  status.profileData?.onboardingSkipped;
      if (onboardingCompleted || onboardingSkipped) { onComplete?.(); return; }
    } catch (error) {
      logger.error('❌ [Onboarding] Error checking status:', error);
    }
  };

  const transition = (fn) => {
    setIsTransitioning(true);
    setTimeout(() => { fn(); setIsTransitioning(false); }, 220);
  };

  const goTo = (step) => transition(() => setCurrentStep(step));

  const nextStep = () => {
    const steps = Object.values(ONBOARDING_STEPS);
    const idx = steps.indexOf(currentStep);
    if (idx < steps.length - 1) goTo(steps[idx + 1]);
  };

  const prevStep = () => {
    const steps = Object.values(ONBOARDING_STEPS);
    const idx = steps.indexOf(currentStep);
    if (idx > 0) goTo(steps[idx - 1]);
  };

  const validateStep = () => {
    const errs = {};
    if (currentStep === ONBOARDING_STEPS.BIRTH_DATE) {
      if (!formData.birthDate) {
        errs.birthDate = 'Birth date is required';
      } else if (new Date(formData.birthDate) > new Date()) {
        errs.birthDate = 'Birth date cannot be in the future';
      }
    }
    if (currentStep === ONBOARDING_STEPS.BIRTH_TIME && formData.birthTime && !formData.skipBirthTime) {
      if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.birthTime)) {
        errs.birthTime = 'Use HH:MM format (e.g. 14:30)';
      }
    }
    if (currentStep === ONBOARDING_STEPS.CONSENT && !ageVerified) {
      errs.ageVerified = 'Please confirm your age and accept the terms to continue';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep === ONBOARDING_STEPS.CONSENT) {
      calculateProfile();
    } else {
      nextStep();
    }
  };

  const handleSkipBirthTime = () => {
    setFormData({ ...formData, skipBirthTime: true, birthTime: '' });
    nextStep();
  };

  const calculateProfile = async () => {
    if (user) {
      saveConsent(user.uid, {
        termsAccepted: true,
        privacyAccepted: true,
        ageVerified: true
      }).catch(() => {});
    }

    goTo(ONBOARDING_STEPS.CALCULATING);

    try {
      const profileData = {
        birthDate: formData.birthDate,
        birthTime: formData.skipBirthTime ? '' : formData.birthTime,
        birthPlace: formData.birthPlace,
        onboardingCompleted: true,
        uid: user.uid
      };
      if (formData.birthPlaceData) {
        profileData.birthPlaceLat = formData.birthPlaceData.lat;
        profileData.birthPlaceLon = formData.birthPlaceData.lon;
        profileData.birthTimezone = formData.birthPlaceData.timezone;
      }
      await saveProfile(profileData);
      setTimeout(() => goTo(ONBOARDING_STEPS.REVEAL), 2800);
    } catch (error) {
      logger.error('Error saving profile:', error);
      setErrors({ submit: 'Something went wrong. Please try again.' });
      goTo(ONBOARDING_STEPS.CONSENT);
    }
  };

  const handleComplete = async () => {
    onComplete?.({
      stepsCompleted: 5,
      hasBirthDate: !!formData.birthDate,
      hasBirthTime: !!formData.birthTime && !formData.skipBirthTime,
      hasBirthPlace: !!formData.birthPlace
    });
  };

  const handleSkip = async () => {
    onSkip?.({ skippedAtStep: currentStep });
  };

  const PROGRESS_STEPS = [
    ONBOARDING_STEPS.WELCOME,
    ONBOARDING_STEPS.BIRTH_DATE,
    ONBOARDING_STEPS.BIRTH_TIME,
    ONBOARDING_STEPS.BIRTH_PLACE,
    ONBOARDING_STEPS.CONSENT,
  ];
  const showProgress = PROGRESS_STEPS.includes(currentStep);
  const progressIndex = PROGRESS_STEPS.indexOf(currentStep);

  const stepClass = `ob-step ${isTransitioning ? 'ob-step--out' : 'ob-step--in'}`;

  return (
    <div className="ob-overlay">
      <div className="ob-scroll">
        <div className="ob-inner">

          {showProgress && (
            <div className="ob-dots" aria-hidden="true">
              {PROGRESS_STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`ob-dot ${i === progressIndex ? 'ob-dot--active' : i < progressIndex ? 'ob-dot--done' : ''}`}
                />
              ))}
            </div>
          )}

          {/* ── WELCOME ─────────────────────────── */}
          {currentStep === ONBOARDING_STEPS.WELCOME && (
            <div className={stepClass}>
              <p className="ob-eyebrow">CLARITY</p>
              {firstName ? (
                <h1 className="ob-title">Welcome,<br />{firstName}.</h1>
              ) : (
                <h1 className="ob-title">Your journal,<br />personalized.</h1>
              )}
              <p className="ob-body">
                Your thoughts deserve more than a blank page. Clarity turns them into something you can actually learn from.
              </p>
              <p className="ob-body ob-body--muted">
                Takes about a minute. You can skip any step.
              </p>
              <div className="ob-actions">
                <button className="ob-btn-primary" onClick={nextStep}>
                  Get started
                </button>
                <button className="ob-btn-ghost" onClick={handleSkip}>
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* ── BIRTH DATE ──────────────────────── */}
          {currentStep === ONBOARDING_STEPS.BIRTH_DATE && (
            <div className={stepClass}>
              <p className="ob-eyebrow">BIRTH DATE</p>
              <h2 className="ob-title">When were you born?</h2>
              <p className="ob-body ob-body--muted">
                Your birth date determines your Sun sign and anchors your chart.
              </p>

              <div className="ob-field">
                <label htmlFor="birthDate" className="ob-label">Date of birth</label>
                <input
                  id="birthDate"
                  type="date"
                  className={`ob-input ${errors.birthDate ? 'ob-input--error' : ''}`}
                  value={formData.birthDate}
                  onChange={e => {
                    setFormData({ ...formData, birthDate: e.target.value });
                    if (errors.birthDate) setErrors(p => ({ ...p, birthDate: null }));
                  }}
                  max={new Date().toISOString().split('T')[0]}
                  aria-required="true"
                />
                {errors.birthDate && <span className="ob-error">{errors.birthDate}</span>}
              </div>

              {formData.birthDate && (
                <div className="ob-zodiac-badge">
                  {getZodiacPreview(formData.birthDate)}
                </div>
              )}

              <div className="ob-actions">
                <button className="ob-btn-back" onClick={prevStep}>Back</button>
                <button className="ob-btn-primary" onClick={handleNext}>Continue</button>
              </div>
            </div>
          )}

          {/* ── BIRTH TIME ──────────────────────── */}
          {currentStep === ONBOARDING_STEPS.BIRTH_TIME && (
            <div className={stepClass}>
              <p className="ob-eyebrow">BIRTH TIME</p>
              <h2 className="ob-title">What time?</h2>
              <p className="ob-body ob-body--muted">
                Your birth time unlocks your Rising sign and Moon placement.
                Most birth certificates list it — otherwise, skip this.
              </p>

              <div className="ob-field">
                <label htmlFor="birthTime" className="ob-label">
                  Time of birth <span className="ob-optional">(optional)</span>
                </label>
                <input
                  id="birthTime"
                  type="time"
                  className={`ob-input ${errors.birthTime ? 'ob-input--error' : ''} ${formData.skipBirthTime ? 'ob-input--disabled' : ''}`}
                  value={formData.birthTime}
                  onChange={e => setFormData({ ...formData, birthTime: e.target.value, skipBirthTime: false })}
                  disabled={formData.skipBirthTime}
                  aria-required="false"
                />
                {errors.birthTime && <span className="ob-error">{errors.birthTime}</span>}
              </div>

              <div className="ob-actions">
                <button className="ob-btn-back" onClick={prevStep}>Back</button>
                <button className="ob-btn-ghost" onClick={handleSkipBirthTime}>I don't know</button>
                <button className="ob-btn-primary" onClick={handleNext}>Continue</button>
              </div>
            </div>
          )}

          {/* ── BIRTH PLACE ─────────────────────── */}
          {currentStep === ONBOARDING_STEPS.BIRTH_PLACE && (
            <div className={stepClass}>
              <p className="ob-eyebrow">BIRTH PLACE</p>
              <h2 className="ob-title">Where?</h2>
              <p className="ob-body ob-body--muted">
                Your birth location helps calculate your Rising sign and astrological houses.
                You can add this later in settings.
              </p>

              <div className="ob-field">
                <label htmlFor="birthPlace" className="ob-label">
                  City or town <span className="ob-optional">(optional)</span>
                </label>
                <CityAutocomplete
                  value={formData.birthPlace}
                  onChange={locationData => setFormData({
                    ...formData,
                    birthPlace: locationData.name,
                    birthPlaceData: locationData
                  })}
                  placeholder="e.g. New York, NY"
                  className="ob-input"
                />
              </div>

              <div className="ob-actions">
                <button className="ob-btn-back" onClick={prevStep}>Back</button>
                <button className="ob-btn-primary" onClick={handleNext}>Continue</button>
              </div>
            </div>
          )}

          {/* ── CONSENT ─────────────────────────── */}
          {currentStep === ONBOARDING_STEPS.CONSENT && (
            <div className={stepClass}>
              <p className="ob-eyebrow">ALMOST DONE</p>
              <h2 className="ob-title">One last thing.</h2>
              <p className="ob-body ob-body--muted">
                Before we build your profile, please confirm the following.
              </p>

              {/* Tappable row — same pattern as InsightSettings frequency selector */}
              <button
                type="button"
                onClick={() => {
                  setAgeVerified(v => !v);
                  if (errors.ageVerified) setErrors(p => ({ ...p, ageVerified: null }));
                }}
                className={`w-full text-left px-4 py-3.5 mt-6 border rounded-xl transition-all duration-200 ${
                  ageVerified
                    ? 'bg-[#ffb85e]/10 border-[#ffb85e]/40'
                    : 'bg-white/[0.03] border-white/10 hover:bg-white/5'
                }`}
                aria-pressed={ageVerified}
              >
                <div className="flex items-start gap-3">
                  {/* Custom radio-style indicator */}
                  <span className={`mt-0.5 w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
                    ageVerified ? 'border-[#ffb85e] bg-[#ffb85e]' : 'border-white/25 bg-transparent'
                  }`}>
                    {ageVerified && (
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path d="M1 3L3 5L7 1" stroke="#1a1410" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                  <span className={`text-sm font-light leading-relaxed transition-colors duration-200 ${ageVerified ? 'text-white' : 'text-white/60'}`}>
                    I'm 13 or older and agree to the{' '}
                    <a
                      href="/legal/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ff9e5e]/80 underline decoration-[#ff9e5e]/30 underline-offset-2 hover:text-[#ff9e5e]"
                      onClick={e => e.stopPropagation()}
                    >Terms of Service</a>
                    {' '}and{' '}
                    <a
                      href="/legal/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ff9e5e]/80 underline decoration-[#ff9e5e]/30 underline-offset-2 hover:text-[#ff9e5e]"
                      onClick={e => e.stopPropagation()}
                    >Privacy Policy</a>.
                  </span>
                </div>
              </button>

              {errors.ageVerified && (
                <p className="ob-error ob-error--field" role="alert">{errors.ageVerified}</p>
              )}
              {errors.submit && (
                <p className="ob-error ob-error--field" role="alert">{errors.submit}</p>
              )}

              <div className="ob-actions">
                <button className="ob-btn-back" onClick={prevStep}>Back</button>
                <button className="ob-btn-primary" onClick={handleNext}>Create my profile</button>
              </div>
            </div>
          )}

          {/* ── CALCULATING ─────────────────────── */}
          {currentStep === ONBOARDING_STEPS.CALCULATING && (
            <div className={`${stepClass} ob-step--center`} role="status" aria-live="polite">
              <div className="ob-orbit-wrap" aria-hidden="true">
                <div className="ob-orbit ob-orbit--1"><Sun size={18} className="ob-planet" /></div>
                <div className="ob-orbit ob-orbit--2"><Moon size={16} className="ob-planet" /></div>
                <div className="ob-orbit ob-orbit--3"><Star size={14} className="ob-planet" /></div>
              </div>
              <h2 className="ob-title ob-title--center">Building your chart…</h2>
              <p className="ob-body ob-body--muted ob-body--center">
                Calculating planetary positions and preparing your insights.
              </p>
            </div>
          )}

          {/* ── REVEAL ──────────────────────────── */}
          {currentStep === ONBOARDING_STEPS.REVEAL && (
            <div className={stepClass}>
              <p className="ob-eyebrow">YOUR PROFILE</p>
              <h1 className="ob-title">You're all set.</h1>
              <p className="ob-body ob-body--muted">
                Your chart is ready. Every insight going forward will be grounded
                in your astrological profile.
              </p>

              {hexagramProfile && (
                <div className="ob-reveal-cards">
                  <div className="ob-reveal-card clarity-reveal-1">
                    <span className="ob-reveal-label">Sun sign</span>
                    <span className="ob-reveal-value">{hexagramProfile.zodiacSign || '—'}</span>
                  </div>
                  {hexagramProfile.humanDesign && (
                    <div className="ob-reveal-card clarity-reveal-2">
                      <span className="ob-reveal-label">Human Design</span>
                      <span className="ob-reveal-value">{hexagramProfile.humanDesign.type}</span>
                    </div>
                  )}
                  {hexagramProfile.humanDesign?.strategy && (
                    <div className="ob-reveal-card clarity-reveal-3">
                      <span className="ob-reveal-label">Strategy</span>
                      <span className="ob-reveal-value">{hexagramProfile.humanDesign.strategy}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="ob-actions">
                <button className="ob-btn-primary" onClick={handleComplete}>
                  Start journaling
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function getZodiacPreview(dateString) {
  if (!dateString) return '';
  return calculateZodiacSign(new Date(dateString));
}

export default Onboarding;
